import React, { memo, useState, useCallback, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Text, Box } from '@atlaskit/primitives/compiled';
import { SingleLineTextInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-single-line-text-inline-edit-full/src/ui/single-line-text/index.tsx';
import type { plainText_nativeIssueTable_PlainTextCell_fieldRef$key } from '@atlassian/jira-relay/src/__generated__/plainText_nativeIssueTable_PlainTextCell_fieldRef.graphql';
import type { plainText_nativeIssueTable_PlainTextCellOld_fieldRef$key } from '@atlassian/jira-relay/src/__generated__/plainText_nativeIssueTable_PlainTextCellOld_fieldRef.graphql';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import { HoverPopover } from '@atlassian/jira-hover-popover/src/ui/index.tsx';
import mergeRefs from '@atlassian/jira-merge-refs/src/index.tsx';
import { TruncatedTextWithPopup } from '../../truncated-text-cell-with-popup/index.tsx';
import {
	useIsDensityFull,
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import { MAX_LINE_WRAP_NEW } from '../../../constants.tsx';

// Attributes for inline editing track events
const attributes = {
	// If the update was made inline (and not via the issue view)
	isInlineEditing: true,
};

export type Props = {
	fieldRef: plainText_nativeIssueTable_PlainTextCell_fieldRef$key;
	isReadOnlyWithoutHoverPopover?: boolean;
};

export const PlainTextCell = memo<Props>(({ fieldRef, isReadOnlyWithoutHoverPopover }: Props) => {
	const data = useFragment<plainText_nativeIssueTable_PlainTextCell_fieldRef$key>(
		graphql`
			fragment plainText_nativeIssueTable_PlainTextCell_fieldRef on JiraSingleLineTextField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				...plainText_nativeIssueTable_PlainTextCellOld_fieldRef
				...singleLineText_issueFieldSingleLineTextInlineEditFull_SingleLineTextInlineEditViewWithIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);

	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();

	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable =
		'lazyIsEditableInIssueView' in data && isInlineEditingExtendedFieldSupportEnabled
			? data.lazyIsEditableInIssueView
			: false;
	useLazyLoadEditability(data.id, isEditable);

	const isDensityCompact = !useIsDensityFull();
	const contentRef = useRef<HTMLElement | null>(null);
	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();

	const renderCustomReadView = useCallback(
		(text: string | null) => {
			if (!text) {
				return <EmptyFieldText />;
			}

			if (isSingleLineRowHeightEnabled) {
				return (
					<HoverPopover content={text} contentRef={contentRef}>
						{({ ref }) => (
							<Text
								ref={mergeRefs(contentRef, ref)}
								maxLines={MAX_LINE_WRAP_NEW}
								testId="native-issue-table.common.ui.issue-cells.plain-text.text"
							>
								{text}
							</Text>
						)}
					</HoverPopover>
				);
			}

			return <Text maxLines={isDensityCompact ? 2 : undefined}>{text}</Text>;
		},
		[isDensityCompact, isSingleLineRowHeightEnabled],
	);
	if (!isInlineEditingExtendedFieldSupportEnabled) {
		return <PlainTextCellOld fieldRef={data} />;
	}
	return (
		<SingleLineTextInlineEditViewWithIsEditable
			attributes={attributes}
			spacing="default"
			editViewPopup
			editViewPopupAlignBlock="center"
			fragmentRef={data}
			isEditable={(isEditable && !isReadOnlyWithoutHoverPopover) ?? false}
			maxLength={255}
			readViewFitContainerHeight
			renderCustomReadView={isReadOnlyWithoutHoverPopover ? undefined : renderCustomReadView}
			hideEditButton
			hasReadViewTruncateDisabled={isSingleLineRowHeightEnabled}
		/>
	);
});

export type PlainTextCellOldProps = {
	fieldRef: plainText_nativeIssueTable_PlainTextCellOld_fieldRef$key;
	/**
	 * Should only be needed For VR testing, to render a storybook example with
	 * the popup content immediately visible
	 */
	shouldOpenOnFirstRender?: boolean;
};

export const PlainTextCellOld = memo<PlainTextCellOldProps>(
	({ fieldRef, shouldOpenOnFirstRender = false }: PlainTextCellOldProps) => {
		const { text } = useFragment<plainText_nativeIssueTable_PlainTextCellOld_fieldRef$key>(
			graphql`
				fragment plainText_nativeIssueTable_PlainTextCellOld_fieldRef on JiraSingleLineTextField {
					text
				}
			`,
			fieldRef,
		);
		const [isOpen, setIsOpen] = useState(shouldOpenOnFirstRender);
		const contentRef = useRef<HTMLElement | null>(null);
		const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();

		if (isSingleLineRowHeightEnabled) {
			return (
				<HoverPopover content={text} contentRef={contentRef}>
					{({ ref }) => (
						<Box paddingBlock="space.050" paddingInline="space.100">
							<Text
								ref={mergeRefs(contentRef, ref)}
								maxLines={MAX_LINE_WRAP_NEW}
								testId="native-issue-table.common.ui.issue-cells.plain-text.text"
							>
								{text}
							</Text>
						</Box>
					)}
				</HoverPopover>
			);
		}

		return (
			<TruncatedTextWithPopup
				isOpen={isOpen}
				onSetIsOpen={setIsOpen}
				// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
				content={<p>{text}</p>}
				triggerText={text ?? null}
			/>
		);
	},
);
